import { Time } from "@angular/common";
import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Store } from "@ngrx/store";
import moment from "moment";
import { BehaviorSubject, Observable, Subscription } from "rxjs";
import { filter, map, tap, withLatestFrom } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { AppSettings } from "src/components/shared-components/class/general-classes/app-settings";
import { GenericApiData } from "src/components/shared-components/class/general-classes/generic-api-data";
import { GroupTimeSlot } from "src/model/class/general classes/group-time-slot";
import { AccessStrategy } from "src/components/shared-components/enum/enumAccessControl";
import { LogLevel } from "src/components/shared-components/enum/enumLog";
import { ManufacturerSystem } from "src/components/shared-components/enum/enumMisc";
import { TimeSlotKind } from "src/model/enum/enumTimeSlot";
import { EnvironmentAccessControlMaintanance } from "src/model/interface/environment-access-control-maintanance";
import { GenericStoreApiData } from "src/components/shared-components/interfaces/generic-store-api-data";
import { iEvent } from "src/components/shared-components/interfaces/iEvent";
import { TimeSlot, TimeSlot1, Timeslot24 } from "src/model/interface/timeslot";
import {
  groupTimeSlotLoadInitStartAction,
  timeSlotLoadInitStartAction,
} from "src/state/actions/time-slot.action";
import { isUserAuthenticated } from "src/state/selectors/authentication.selectors";
import { listTimeSlot } from "src/state/selectors/time-slot.selector";
import { EventService } from "./event.service";
import { LocalStorageService } from "./local-storage.service";
import { LoggerService } from "./logger.service";
import { MomentFormats } from "src/model/class/general classes/moment-formats";

/**
 * Generic time slot service
 */
@Injectable({
  providedIn: "root",
})
export class TimeslotService implements iEvent {
  private _logContext: string = "Timeslot.service";

  private _listTimeSlotSubscribtion: Subscription = null;

  private _listTimeSlot: Array<TimeSlot> = null;

  public _eventSubscriptions: Array<Subscription> = null;

  constructor(
    private _http: HttpClient,
    private _store: Store,
    private _eventService: EventService,
    private _loggerService: LoggerService
  ) {
    this.loadListTimeSlot();
    this.subscribeEvents();
  }

  ngOnDestroy() {
    this.unsubscribeStoreSelect();
    this.unsubscribeEvents();
  }

  /**
   *  Initialize public observable list trasmission: it should be call in the costructor of all component/page that you need.
   */
  public Init(): void {
    this._store.dispatch(timeSlotLoadInitStartAction());
    this._store.dispatch(groupTimeSlotLoadInitStartAction());
  }

  /**
   * Reload service data (es. in case of error)
   */
  public reloadServiceData(): void {
    this.Init();
  }

  /**
   * Get timeslot data from API. If api call fallback in error costructor, it set the appropriate flag "_isErrorInGetTimeSlotListAPIRequest" to
   * TRUE, it set the store to NULL and emit on the osbservable a neutral value (IE. empty array).
   */
  public getTimeslot(): Observable<GenericApiData> {
    return this._http.get<GenericApiData>(
      environment.httpServerRoot + AppSettings.API_URL_TIMESLOT
    );
  }

  /**
   * Get timeslot data from API with last modify data store.
   * If api call fallback in error costructor, it set the appropriate flag "_isErrorInGetTimeSlotListAPIRequest" to
   * TRUE, it leaves the store as it is and emit on the osbservable a neutral value (IE. empty array).
   * If original data store isn't changed, api return nothin (code 200)
   *
   * @param timeslotStore Timeslot store
   * @returns
   */
  public getTimeslotByLastModify(
    timeslotStore: GenericApiData
  ): Observable<GenericApiData> {
    const lastAPIModifiedString: string = (timeslotStore?.lastAPIModified || "");

    const queryString: string =
      "?lastModified=" + encodeURIComponent(lastAPIModifiedString);

    return this._http
      .get<GenericApiData>(
        environment.httpServerRoot + AppSettings.API_URL_TIMESLOT + queryString
      )
      .pipe(
        map((returnTimeSlotStore: GenericApiData) => {
          //if initial resource isn't changed it return null value (returnNotificationStore = null)
          if (returnTimeSlotStore) {
            timeslotStore = returnTimeSlotStore;
          }

          return timeslotStore;
        })
      );
  }

  /**
   *  GetGroupTimeSlot from API. If api call fallback in error costructor, it set the appropriate flag "_isErrorInGetGroupsOfTimeSlotsListAPIRequest" to
   * TRUE, it set the store to NULL and emit on the osbservable a neutral value (IE. empty array).
   */
  public getGroupTimeSlot(): Observable<GenericApiData> {
    return this._http.get<GenericApiData>(
      environment.httpServerRoot + AppSettings.API_URL_GROUP_TIMESLOT
    );
  }

  /**
   * GetGroupTimeSlotByLastModify from API. If api call fallback in error costructor, it set the appropriate flag "_isErrorInGetGroupsOfTimeSlotsListAPIRequest" to
   * TRUE, it leaves the store as it is and emit on the osbservable a neutral value (IE. empty array).
   */
  public getGroupTimeSlotByLastModify(
    groupTimeSlotStore: GenericApiData
  ): Observable<GenericApiData> {
    const queryString: string =
      "?lastModified=" + encodeURIComponent(groupTimeSlotStore.lastAPIModified);

    return this._http
      .get<GenericApiData>(
        environment.httpServerRoot +
        AppSettings.API_URL_GROUP_TIMESLOT +
        queryString
      )
      .pipe(
        map((returnGroupTimeSlotStore: GenericApiData) => {
          //if initial resource isn't changed it return null value (returnNotificationStore = null)
          if (returnGroupTimeSlotStore) {
            groupTimeSlotStore = returnGroupTimeSlotStore;
          }

          return groupTimeSlotStore;
        })
      );
  }

  /**
   * Check if specific datetime (formatted "YYYY-MM-DD HH:mm:ss) is allowed for specific timeslot
   *
   * @param idTimeSlot ID timeslot to check
   * @param datetime Date time to check
   * @returns
   */
  public isDateTimeInTimeSlot(
    idTimeSlot: number,
    datetime: string = moment().format(MomentFormats.MOMENT_DATETIME_FORMAT)
  ): boolean {
    let isDateInTimeSlot: boolean = false;

    //When time slot is lower then 1, time slot is "All"
    if (idTimeSlot > 1) {
      const foundedTimeSlot: TimeSlot = this._listTimeSlot.find(
        (timeslot: TimeSlot) => timeslot.id === idTimeSlot
      );

      if (foundedTimeSlot) {
        if (foundedTimeSlot.kind === TimeSlotKind.TIMESLOT_24) {
          isDateInTimeSlot = this.checkIfDateTimeIsInTimeSlot24(
            foundedTimeSlot,
            datetime
          );
        } else if (foundedTimeSlot.kind === TimeSlotKind.TIMESLOT_1) {
          isDateInTimeSlot = this.checkIfDateTimeIsInTimeSlot1(
            foundedTimeSlot,
            datetime
          );
        }
      }
    } else {
      isDateInTimeSlot = true;
    }

    return isDateInTimeSlot;
  }

  //#region Event Service
  public subscribeEvents(): void {
    this._eventSubscriptions = new Array<Subscription>();

    this._eventSubscriptions.push(
      this._eventService.OnComeBackOnlineMqtt.pipe(
        withLatestFrom(this._store.select(isUserAuthenticated)),
        filter((combineArray: Array<any>) => combineArray[1])
      ).subscribe(() => {
        this._store.dispatch(timeSlotLoadInitStartAction());
      })
    );
  }

  /**
   * Check type of time slot based by Manufacturer system and accessStrategy
   * @param manufacturer
   * @param environment
   * @returns
   */
  public getKindTimeSlotByManufacturerAndEnvironment(
    manufacturer: ManufacturerSystem,
    environment: EnvironmentAccessControlMaintanance
  ): TimeSlotKind {
    if (
      environment.accessStrategy == AccessStrategy.CENTRALIZED_BLACK_LIST ||
      environment.accessStrategy == AccessStrategy.CENTRALIZED_WHITE_LIST
    ) {
      return TimeSlotKind.TIMESLOT_1;
    } else {
      if (
        manufacturer == ManufacturerSystem.ABB_MILLENIUM ||
        manufacturer == ManufacturerSystem.ABB_MAC2 ||
        manufacturer == ManufacturerSystem.ABB_TACTEO ||
        manufacturer == ManufacturerSystem.MQTT ||
        manufacturer == ManufacturerSystem.BIG_VIRTUAL_KEYPAD ||
        manufacturer == ManufacturerSystem.GENERIC_KEYPAD ||
        manufacturer == ManufacturerSystem.BIG_VIRTUAL_MIFARE ||
        manufacturer == ManufacturerSystem.BIG_VIRTUAL
      ) {
        return TimeSlotKind.TIMESLOT_1;
      } else if (
        manufacturer == ManufacturerSystem.VIMAR_EELECTRON ||
        manufacturer == ManufacturerSystem.VIMAR_WELL_CONTACT_PLUS ||
        manufacturer == ManufacturerSystem.SCHNEIDER ||
        manufacturer == ManufacturerSystem.SIEMENS ||
        manufacturer == ManufacturerSystem.EELECTRON ||
        manufacturer == ManufacturerSystem.EELECTRON_KEYPAD ||
        manufacturer == ManufacturerSystem.ELECTRONE ||
        manufacturer == ManufacturerSystem.GEWISS
      ) {
        return TimeSlotKind.TIMESLOT_24;
      } else {
        return TimeSlotKind.UNKNOWN;
      }
    }
  }

  public unsubscribeEvents(): void {
    for (let sub of this._eventSubscriptions) {
      sub.unsubscribe();
    }
  }
  //#endregion

  /**
   * Load full list of time slot
   */
  private loadListTimeSlot(): void {
    this._listTimeSlotSubscribtion = this._store
      .select(listTimeSlot)
      .pipe(
        filter(
          (listTimeSlot: GenericStoreApiData) => !!listTimeSlot
        ),
        filter((listTimeSlot: GenericStoreApiData) => listTimeSlot?.loaded),
        tap((listTimeSlot: GenericStoreApiData) => {
          if (
            listTimeSlot.errorGeneric ||
            listTimeSlot.errorNotYetImplemented
          ) {
            this.handleApiResponseError();
          }
        }),
        filter(
          (listTimeSlot: GenericStoreApiData) =>
            !(listTimeSlot.errorGeneric || listTimeSlot.errorNotYetImplemented)
        ),
        tap((listTimeSlot: GenericStoreApiData) => {
          if (listTimeSlot.isOldDataInUse) {
            this._loggerService.log(
              LogLevel.LOG_INFO,
              this._logContext,
              "_store.select(listTimeSlot)",
              "loadListTimeSlot()",
              "We are using old data!!"
            );
          }
        }),
        map(
          (listTimeSlot: GenericStoreApiData) => listTimeSlot.data?.data || []
        )
      )
      .subscribe((listTimeSlot: Array<TimeSlot>) => {
        this._listTimeSlot = listTimeSlot;
      });
  }

  /**
   * Return list filtered by kind
   * @param kind
   */
  public getListByKind(kind: TimeSlotKind): TimeSlot[] {
    if (kind == TimeSlotKind.TIMESLOT_1) {
      return this._listTimeSlot.filter((timeSlot) => {
        return timeSlot.kind === kind && timeSlot.name !== "ALL";
      });
    } else {
      return this._listTimeSlot.filter((timeSlot) => {
        return timeSlot.kind === kind;
      });
    }
  }

  private handleApiResponseError(): void {
    this._listTimeSlot = [];
  }

  /**
   * Handle unsubscription from store
   */
  private unsubscribeStoreSelect(): void {
    this._listTimeSlotSubscribtion?.unsubscribe();
  }

  /**
   * Check if date time is valid in specific time slot 1
   *
   * @param timeSlot Time slot to compare
   * @param datetime Date time to check validity
   * @returns True if specific date time is valid on input time slot
   */
  private checkIfDateTimeIsInTimeSlot1(
    timeSlot: TimeSlot,
    datetime: string
  ): boolean {
    let isDateInTimeSlot: boolean = false;
    let index: number = 0;
    const dayOfWeek: number = moment(
      datetime,
      MomentFormats.MOMENT_DATETIME_FORMAT
    ).isoWeekday();
    const hourMinute: string = moment(
      datetime,
      MomentFormats.MOMENT_DATETIME_FORMAT
    ).format(MomentFormats.MOMENT_DATETIME_FORMAT_LONG_HOUR_MINUTE_SECONDS);

    while (!isDateInTimeSlot && index < timeSlot.listTimeSlot1.length) {
      isDateInTimeSlot = this.checkTimeSlotInDayOfWeek(
        timeSlot.listTimeSlot1[index],
        dayOfWeek
      );

      if (isDateInTimeSlot) {
        isDateInTimeSlot = this.checkTimeSlot1InHourRange(
          timeSlot.listTimeSlot1[index],
          hourMinute
        );
      }

      index++;
    }

    return isDateInTimeSlot;
  }

  /**
   * Check if date time is valid in specific time slot 24
   *
   * @param timeSlot Time slot to compare
   * @param datetime Date time to check validity
   * @returns True if specific date time is valid on input time slot
   */
  private checkIfDateTimeIsInTimeSlot24(
    timeSlot: TimeSlot,
    datetime: string
  ): boolean {
    let isDateInTimeSlot: boolean = false;

    const dayOfWeek: number = moment(
      datetime,
      MomentFormats.MOMENT_DATETIME_FORMAT
    ).isoWeekday();
    const hour: number = moment(
      datetime,
      MomentFormats.MOMENT_DATETIME_FORMAT
    ).hour();

    isDateInTimeSlot = this.checkTimeSlotInDayOfWeek(
      timeSlot.timeSlot24,
      dayOfWeek
    );

    if (isDateInTimeSlot) {
      isDateInTimeSlot = this.checkTimeSlot24InHour(timeSlot, hour);
    }

    return isDateInTimeSlot;
  }

  /**
   * Check if specific day is in timeslot
   *
   * @param timeslot Timeslot to check
   * @param dayOfWeek Day of week (from 1... to 7)
   * @returns Is day is on week
   */
  private checkTimeSlotInDayOfWeek(
    timeslot: TimeSlot1 | Timeslot24,
    dayOfWeek: number
  ): boolean {
    let isDayWeekInTimeSlot: boolean = false;

    switch (dayOfWeek) {
      case 1:
        isDayWeekInTimeSlot = timeslot.monday;
        break;
      case 2:
        isDayWeekInTimeSlot = timeslot.tuesday;
        break;
      case 3:
        isDayWeekInTimeSlot = timeslot.wednesday;
        break;
      case 4:
        isDayWeekInTimeSlot = timeslot.thursday;
        break;
      case 5:
        isDayWeekInTimeSlot = timeslot.friday;
        break;
      case 6:
        isDayWeekInTimeSlot = timeslot.saturday;
        break;
      case 7:
        isDayWeekInTimeSlot = timeslot.sunday;
        break;
    }

    return isDayWeekInTimeSlot;
  }

  /**
   * Check if 24 time slot if valid on specific hour
   *
   * @param timeslot Timeslot to check
   * @param hour Horu to check
   * @returns Hour property time slot
   */
  private checkTimeSlot24InHour(timeslot: TimeSlot, hour: number): boolean {
    return timeslot["hour" + hour];
  }

  /**
   * Check if specific hour minute (HH:mm) is inside range between time slot 1 start time
   * and end time
   *
   * @param timeSlot1 Time slot 2 to check
   * @param hourMinute Hour minute (HH:mm) to check
   * @returns
   */
  private checkTimeSlot1InHourRange(
    timeSlot1: TimeSlot1,
    hourMinute: string
  ): boolean {
    return (
      moment(
        hourMinute,
        MomentFormats.MOMENT_DATETIME_FORMAT_LONG_HOUR_MINUTE_SECONDS
      ).isSameOrAfter(
        moment(
          moment(
            timeSlot1.startTime,
            MomentFormats.MOMENT_DATETIME_FORMAT_DEVICE_VALID_LIST
          ).format(
            MomentFormats.MOMENT_DATETIME_FORMAT_LONG_HOUR_MINUTE_SECONDS
          ),
          MomentFormats.MOMENT_DATETIME_FORMAT_LONG_HOUR_MINUTE_SECONDS
        )
      ) &&
      moment(
        hourMinute,
        MomentFormats.MOMENT_DATETIME_FORMAT_LONG_HOUR_MINUTE_SECONDS
      ).isSameOrBefore(
        moment(
          moment(
            timeSlot1.endTime,
            MomentFormats.MOMENT_DATETIME_FORMAT_DEVICE_VALID_LIST
          ).format(
            MomentFormats.MOMENT_DATETIME_FORMAT_LONG_HOUR_MINUTE_SECONDS
          ),
          MomentFormats.MOMENT_DATETIME_FORMAT_LONG_HOUR_MINUTE_SECONDS
        )
      )
    );
  }
}
